import { FormikConfig } from 'formik';
import { memo, useCallback, useMemo, useState } from 'react';
import * as Yup from 'yup';
import { CreateModal } from '../../components/Modal/CreateModal';
import { FormikInput } from '../../components/formik/FormInput';
import { T, useT } from '../../translation/src';
import { useNavvisAuthenticateMutation, NavVisAuthResult } from '../../types/graphqlTypes';

interface NavVisLoginModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (authResult: NavVisAuthResult) => void;
}

interface FormValues {
  username: string;
  password: string;
  baseUrl: string;
}

const NavVisLoginModal_: React.FC<NavVisLoginModalProps> = ({ open, onClose, onSuccess }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [authenticate] = useNavvisAuthenticateMutation();

  const usernameLabel = useT('Username');
  const passwordLabel = useT('Password');
  const baseUrlLabel = useT('Server URL (optional)');
  const usernameRequired = useT('Username is required');
  const passwordRequired = useT('Password is required');

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        username: Yup.string().required(usernameRequired),
        password: Yup.string().required(passwordRequired),
        baseUrl: Yup.string(),
      }),
    [usernameRequired, passwordRequired],
  );

  const onSubmit: FormikConfig<FormValues>['onSubmit'] = useCallback(
    async (values, { setFieldError }) => {
      setIsSubmitting(true);
      try {
        const result = await authenticate({
          variables: {
            username: values.username,
            password: values.password,
            baseUrl: values.baseUrl || undefined,
          },
        });

        if (result.data?.navvisAuthenticate) {
          onSuccess(result.data.navvisAuthenticate);
          onClose();
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Authentication failed';
        setFieldError('password', message);
      } finally {
        setIsSubmitting(false);
      }
    },
    [authenticate, onSuccess, onClose],
  );

  const formik: FormikConfig<FormValues> = useMemo(
    () => ({
      initialValues: {
        username: '',
        password: '',
        baseUrl: '',
      },
      validationSchema,
      onSubmit,
    }),
    [validationSchema, onSubmit],
  );

  return (
    <CreateModal<FormValues> open={open} onClose={onClose} title={<T _str="Connect to NavVis IVION" />} formik={formik} isSubmitting={isSubmitting} createButtonTitle={<T _str="Connect" />}>
      <div className="space-y-4">
        <FormikInput name="username" label={usernameLabel} autoComplete="username" />
        <FormikInput name="password" label={passwordLabel} type="password" autoComplete="current-password" />
        <FormikInput name="baseUrl" label={baseUrlLabel} placeholder="partners.iv.navvis.com" />
      </div>
    </CreateModal>
  );
};

export const NavVisLoginModal = memo(NavVisLoginModal_);
